import React from 'react';
import { CONFERENCE_TRACKER_SPORTS } from '../../../types/admin/subscriptionTracker';
import { getSportLabel } from '../../../utils/admin/formatSubscriptionDate';
import EndingSoonRangeControls from './EndingSoonRangeControls';

export type TeamSubStatusPreset =
  | 'all'
  | 'grace'
  | 'grace_ending_soon'
  | 'grace_ended'
  | 'sub_ending_soon'
  | 'ended';

const PRESET_OPTIONS: { value: TeamSubStatusPreset; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'grace', label: 'Grace active' },
  { value: 'grace_ending_soon', label: 'Grace ending soon' },
  { value: 'grace_ended', label: 'Grace expired' },
  { value: 'sub_ending_soon', label: 'Sub ending soon' },
  { value: 'ended', label: 'Sub expired' },
];

interface TeamSubFilterBarProps {
  preset: TeamSubStatusPreset;
  onPresetChange: (value: TeamSubStatusPreset) => void;
  useCustomRange: boolean;
  onUseCustomRangeChange: (value: boolean) => void;
  endsFrom: string;
  endsTo: string;
  onEndsFromChange: (value: string) => void;
  onEndsToChange: (value: string) => void;
  sportCode: string;
  onSportCodeChange: (value: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  listLoading: boolean;
}

const chipClass = (active: boolean, first: boolean, last: boolean) =>
  `px-3 py-1.5 text-sm font-medium border-r border-gray-200 ${
    active ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
  } ${first ? 'rounded-l-md' : ''} ${last ? 'rounded-r-md border-r-0' : ''}`;

const TeamSubFilterBar: React.FC<TeamSubFilterBarProps> = ({
  preset,
  onPresetChange,
  useCustomRange,
  onUseCustomRangeChange,
  endsFrom,
  endsTo,
  onEndsFromChange,
  onEndsToChange,
  sportCode,
  onSportCodeChange,
  search,
  onSearchChange,
  onRefresh,
  listLoading,
}) => {
  const showEndingSoonRange = preset === 'grace_ending_soon' || preset === 'sub_ending_soon';

  return (
    <div className="mb-4 space-y-4">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500 uppercase">Status filter</span>
          <div className="inline-flex rounded-md shadow-sm flex-wrap">
            {PRESET_OPTIONS.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onPresetChange(option.value)}
                className={chipClass(
                  preset === option.value,
                  index === 0,
                  index === PRESET_OPTIONS.length - 1
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {showEndingSoonRange && (
          <EndingSoonRangeControls
            idPrefix="team-sub"
            useCustomRange={useCustomRange}
            onUseCustomRangeChange={onUseCustomRangeChange}
            endsFrom={endsFrom}
            endsTo={endsTo}
            onEndsFromChange={onEndsFromChange}
            onEndsToChange={onEndsToChange}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="w-full sm:w-48">
          <label htmlFor="teamSubSport" className="block text-sm font-medium text-gray-700 mb-1">
            Sport
          </label>
          <select
            id="teamSubSport"
            className="w-full p-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={sportCode}
            onChange={(e) => onSportCodeChange(e.target.value)}
          >
            <option value="">All sports</option>
            {CONFERENCE_TRACKER_SPORTS.map((code) => (
              <option key={code} value={code}>
                {getSportLabel(code)} ({code})
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-[200px] max-w-md">
          <label htmlFor="teamSubSearch" className="block text-sm font-medium text-gray-700 mb-1">
            Team search
          </label>
          <input
            id="teamSubSearch"
            type="text"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Search by team name or code"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        <button
          type="button"
          onClick={onRefresh}
          disabled={listLoading}
          className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>
    </div>
  );
};

export default TeamSubFilterBar;
